import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/SubjectExplorerPage.css';
import { trackEvent } from '../services/analytics';
import SubjectIcon from './SubjectIcon';
import {
  enrichSubjectMetadata,
  LEGACY_SUBJECT_FALLBACK_CODE,
  localizedSubjectLabel,
  normalizeSubjectSearch,
  SUBJECT_CATALOG,
  parseSubjectJsonResponse,
} from '../services/subjectCatalog';

function SubjectDirectoryPage() {
  const navigate = useNavigate();
  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [labelLocale, setLabelLocale] = useState('en');
  const [subjectQuery, setSubjectQuery] = useState('');

  useEffect(() => {
    let ignore = false;

    const fetchSubjects = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await fetch('/api/subjects');
        const data = await parseSubjectJsonResponse(response, 'Unable to fetch subjects.');

        if (!ignore) {
          setSubjects(Array.isArray(data.subjects) ? data.subjects.map(enrichSubjectMetadata) : []);
        }
      } catch (fetchError) {
        if (!ignore) {
          if (fetchError.code === LEGACY_SUBJECT_FALLBACK_CODE) {
            setSubjects(SUBJECT_CATALOG.map((subject) => ({ ...enrichSubjectMetadata(subject), total: 0 })));
            setError('Compatibility mode: using subject routes without live subject counts.');
          } else {
            setSubjects([]);
            setError(fetchError.message || 'Unable to load subjects right now.');
          }
        }
      } finally {
        if (!ignore) {
          setLoading(false);
        }
      }
    };

    trackEvent('subject_directory_viewed', { source: 'subjects-route' });
    fetchSubjects();

    return () => {
      ignore = true;
    };
  }, []);

  const filteredSubjects = subjects.filter((subject) => {
    const needle = normalizeSubjectSearch(subjectQuery);
    if (!needle) {
      return true;
    }
    const haystack = normalizeSubjectSearch([
      subject.label,
      subject.labelHi,
      subject.labelTe,
      subject.description,
      ...(subject.aliases || []),
      subject.key,
    ].join(' '));
    return haystack.includes(needle);
  });

  const handleSubjectOpen = (subject) => {
    trackEvent('subject_selected', { subject: subject.key, source: 'subject-directory-page' });
    navigate(`/subjects/${subject.key}`);
  };

  return (
    <section className="subject-page">
      <div className="subject-page__hero">
        <div>
          {/* <p className="subject-page__eyebrow">Subject Explorer</p> */}
          <h1>Explore by Subject</h1>          
        </div>
        <div className="subject-page__hero-actions">
          <button type="button" className="subject-page__galaxy-btn" onClick={() => navigate('/collection-map')}>
            🌌 View as galaxy map
          </button>
          <button type="button" className="subject-page__home-btn" onClick={() => navigate('/')}>
            Home
          </button>
        </div>
      </div>

      <div className="subject-page__directory">
        <div className="subject-page__section-head">
          <h2>Subject shelves</h2>
          <div className="subject-page__directory-tools">
            <input
              type="text"
              value={subjectQuery}
              onChange={(event) => setSubjectQuery(event.target.value)}
              className="subject-page__directory-search"
              placeholder="Search subjects (English/Hindi/Telugu, transliteration supported)"
            />
            <div className="subject-page__locale-switch" role="group" aria-label="Subject label language">
              <button type="button" className={labelLocale === 'en' ? 'is-active' : ''} onClick={() => setLabelLocale('en')}>EN</button>
              <button type="button" className={labelLocale === 'hi' ? 'is-active' : ''} onClick={() => setLabelLocale('hi')}>HI</button>
              <button type="button" className={labelLocale === 'te' ? 'is-active' : ''} onClick={() => setLabelLocale('te')}>TE</button>
            </div>
          </div>
        </div>

        {loading && <div className="subject-page__state">Loading subjects...</div>}
        {error && !loading && <div className="subject-page__error">{error}</div>}

        {!loading && (
          <div className="subject-grid">
            {filteredSubjects.map((subject) => (
              <button
                key={subject.key}
                type="button"
                className="subject-tile"
                onClick={() => handleSubjectOpen(subject)}
                style={{ '--subject-accent': subject.accent }}
              >
                <span className="subject-tile__icon" aria-hidden="true">
                  <SubjectIcon subjectKey={subject.key} size={24} className="subject-tile__icon-svg" />
                </span>
                <span className="subject-tile__title">{localizedSubjectLabel(subject, labelLocale)}</span>
                <span className="subject-tile__description">{subject.description}</span>
                <span className="subject-tile__count">
                  {typeof subject.total === 'number' && subject.total > 0 ? `${subject.total.toLocaleString()} titles` : 'Open subject page'}
                </span>
              </button>
            ))}
            {!filteredSubjects.length && (
              <div className="subject-page__state">No subjects match this search. Try English, Hindi, Telugu, or transliterated terms.</div>
            )}
          </div>
        )}
      </div>
    </section>
  );
}

export default SubjectDirectoryPage;